import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import type { DayCardPreview, MapConfig } from '../../api/types'
import { kacheloptionen } from '../../lib/kartenquelle'
import { mapImage } from '../export/mapImage'
import { nadelbild } from '../map/nadelbild'

/**
 * The map of a day's route as a JPEG, drawn in the browser (#79).
 *
 * **The same way as the book's map** (`Buchkarte`, `mapImage`): a Leaflet
 * map that nobody sees, off the screen, the tiles from the map server as
 * on every map here -- the first exception in `CLAUDE.md`, and no new
 * one. When the tiles are there (or the map server stays silent for
 * twelve seconds) the map is copied to a canvas and comes back as a data
 * URL; the server stores it as the post's photo.
 *
 * **The published points of the day stand on it as pins** (#80), the same
 * `nadelbild()` as on the map, in the trip's colour for a published point:
 * the pin reads its colour from `--nadel` on the box, and `mapImage()`
 * copies the computed colours into the picture, as it does for the book.
 *
 * `null` when there is nothing to draw or the canvas cannot be read (a
 * tile without CORS): then the card goes out without a picture.
 */
export async function zeichneTagesroute(
  punkte: [number, number][],
  quelle: MapConfig,
  strich: { farbe: string; breite: number; nadel: string },
  nadeln: DayCardPreview['highlights'] = [],
): Promise<string | null> {
  if (punkte.length < 2 || typeof document === 'undefined') return null
  const kasten = document.createElement('div')
  kasten.setAttribute('aria-hidden', 'true')
  kasten.style.cssText = 'position:fixed;left:-10000px;top:0;width:640px;height:400px;'
  kasten.style.setProperty('--nadel', strich.nadel)
  document.body.appendChild(kasten)
  const karte = L.map(kasten, {
    zoomControl: false,
    attributionControl: true,
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
    keyboard: false,
    // No animation: the picture is taken as soon as the tiles are there,
    // and a map still gliding into place would be copied mid-flight.
    zoomAnimation: false,
    fadeAnimation: false,
    markerZoomAnimation: false,
  })
  try {
    const ebene = L.tileLayer(quelle.tile_url, { ...kacheloptionen(quelle), crossOrigin: 'anonymous' })
    const fertig = new Promise<void>((resolve) => {
      let gemeldet = false
      const melde = () => {
        if (gemeldet) return
        gemeldet = true
        resolve()
      }
      ebene.on('load', melde)
      window.setTimeout(melde, 12000)
    })
    ebene.addTo(karte)
    karte.setMaxZoom(Math.min(quelle.max_zoom, 16))
    const linie = L.polyline(punkte, { color: strich.farbe, weight: strich.breite }).addTo(karte)
    // Start and end as small discs on the line, in the line's colour.
    L.circleMarker(punkte[0]!, { radius: 6, color: '#ffffff', weight: 2, fillColor: strich.farbe, fillOpacity: 1 }).addTo(karte)
    L.circleMarker(punkte[punkte.length - 1]!, { radius: 6, color: '#ffffff', weight: 2, fillColor: strich.farbe, fillOpacity: 1 }).addTo(karte)
    for (const nadel of nadeln) {
      L.marker([nadel.lat, nadel.lon], {
        icon: L.divIcon({ className: '', html: nadelbild('nadel'), iconSize: [22, 30], iconAnchor: [11, 30] }),
        keyboard: false,
        interactive: false,
      }).addTo(karte)
    }
    // The frame holds the line; a pin off the line is a point of the day
    // and belongs in the picture as well.
    const rahmen = linie.getBounds()
    for (const nadel of nadeln) rahmen.extend([nadel.lat, nadel.lon])
    karte.fitBounds(rahmen, { padding: [28, 28] })
    await fertig
    // One more frame for the tiles to be painted where Leaflet put them.
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    return await mapImage(kasten, 2)
  } catch {
    return null
  } finally {
    karte.remove()
    kasten.remove()
  }
}
